import type { PartnerType } from './shopCatalog';

export type RelicCategory = '通用' | '职业' | '风险' | '经济';
export type RelicRarity = 'common' | 'fine' | 'rare' | 'epic' | 'legendary';

export type RelicId =
  | 'iron-skin'
  | 'swift-boots'
  | 'magnet'
  | 'whetstone'
  | 'ammo-pouch'
  | 'thorn-armor'
  | 'life-tree'
  | 'shield-shard'
  | 'battle-spirit'
  | 'lightning-chain'
  | 'swordmaster-mark'
  | 'sharpshooter'
  | 'elemental-heart'
  | 'healer-sigil'
  | 'berserker-axe'
  | 'barrage'
  | 'resonance-crystal'
  | 'support-protocol'
  | 'blood-oath'
  | 'greed'
  | 'glass-cannon'
  | 'lone-wolf'
  | 'time-bomb'
  | 'bloodlust'
  | 'gambler-dice'
  | 'forgotten-contract'
  | 'merchant-pass'
  | 'gold-rush'
  | 'free-reroll'
  | 'investment-return'
  | 'small-blade'
  | 'field-ration'
  | 'pickup-bell'
  | 'bronze-scope'
  | 'lucky-chip'
  | 'quick-spring'
  | 'rusty-nail'
  | 'cloth-boots'
  | 'tiny-magnet'
  | 'copper-coin'
  | 'training-weight'
  | 'wooden-shield'
  | 'herb-tea'
  | 'spark-plug'
  | 'chalk-mark'
  | 'warm-glove'
  | 'paper-fan'
  | 'focus-thread'
  | 'sharp-blade'
  | 'precision-ring'
  | 'power-core'
  | 'guardian-buckle'
  | 'collector-bag'
  | 'long-barrel'
  | 'arcane-ink'
  | 'piercing-needle'
  | 'overload-coil'
  | 'vampire-fang'
  | 'gold-magnet'
  | 'soul-link'
  | 'echo-chamber'
  | 'armor-piercer'
  | 'endless-vault'
  | 'world-resonance'
  | 'relic-printer';

export const RELIC_RARITY_META: Record<RelicRarity, { label: string; color: number; weight: number }> = {
  common: { label: '普通', color: 0xc8c8c8, weight: 100 },
  fine: { label: '精良', color: 0x4dff88, weight: 55 },
  rare: { label: '稀有', color: 0x4d8dff, weight: 25 },
  epic: { label: '史诗', color: 0xb56cff, weight: 10 },
  legendary: { label: '传说', color: 0xffd700, weight: 3 },
};

export interface RelicDefinition {
  id: RelicId;
  kind: 'relic';
  name: string;
  category: RelicCategory;
  rarity: RelicRarity;
  tag: string;
  behavior: string;
  description: string;
  cost: number;
  color: number;
  maxStacks?: number;
  diminishingFactor?: number;
}

type RawRelicDefinition = Omit<RelicDefinition, 'rarity'>;

const RELIC_RARITY_BY_ID: Record<RelicId, RelicRarity> = {
  'iron-skin': 'common',
  'swift-boots': 'common',
  magnet: 'common',
  whetstone: 'fine',
  'ammo-pouch': 'fine',
  'thorn-armor': 'common',
  'life-tree': 'common',
  'shield-shard': 'epic',
  'battle-spirit': 'fine',
  'lightning-chain': 'fine',
  'swordmaster-mark': 'fine',
  sharpshooter: 'fine',
  'elemental-heart': 'fine',
  'healer-sigil': 'fine',
  'berserker-axe': 'fine',
  barrage: 'fine',
  'resonance-crystal': 'fine',
  'support-protocol': 'fine',
  'blood-oath': 'rare',
  greed: 'fine',
  'glass-cannon': 'rare',
  'lone-wolf': 'rare',
  'time-bomb': 'fine',
  bloodlust: 'epic',
  'gambler-dice': 'common',
  'forgotten-contract': 'rare',
  'merchant-pass': 'epic',
  'gold-rush': 'common',
  'free-reroll': 'epic',
  'investment-return': 'rare',
  'small-blade': 'common',
  'field-ration': 'common',
  'pickup-bell': 'common',
  'bronze-scope': 'common',
  'lucky-chip': 'common',
  'quick-spring': 'common',
  'rusty-nail': 'common',
  'cloth-boots': 'common',
  'tiny-magnet': 'common',
  'copper-coin': 'common',
  'training-weight': 'common',
  'wooden-shield': 'common',
  'herb-tea': 'common',
  'spark-plug': 'common',
  'chalk-mark': 'common',
  'warm-glove': 'common',
  'paper-fan': 'common',
  'focus-thread': 'common',
  'sharp-blade': 'fine',
  'precision-ring': 'fine',
  'power-core': 'fine',
  'guardian-buckle': 'fine',
  'collector-bag': 'fine',
  'long-barrel': 'rare',
  'arcane-ink': 'rare',
  'piercing-needle': 'rare',
  'overload-coil': 'rare',
  'vampire-fang': 'rare',
  'gold-magnet': 'rare',
  'soul-link': 'epic',
  'echo-chamber': 'epic',
  'armor-piercer': 'epic',
  'endless-vault': 'legendary',
  'world-resonance': 'legendary',
  'relic-printer': 'legendary',
};

export const RELIC_CATALOG: readonly RelicDefinition[] = ([
  {
    id: 'iron-skin',
    kind: 'relic',
    name: '铁皮',
    category: '通用',
    tag: '生存',
    behavior: '鼓励硬扛而非纯闪避',
    description: '最大生命 +20。',
    cost: 8,
    color: 0x9da6a8,
    diminishingFactor: 0.8,
  },
  {
    id: 'swift-boots',
    kind: 'relic',
    name: '加速靴',
    category: '通用',
    tag: '机动',
    behavior: '鼓励主动走位捡材料',
    description: '移动速度 +15%。',
    cost: 6,
    color: 0x66b8ff,
    diminishingFactor: 0.7,
  },
  {
    id: 'magnet',
    kind: 'relic',
    name: '磁铁',
    category: '通用',
    tag: '拾取',
    behavior: '减少捡材料的冒险',
    description: '拾取范围 +40%。',
    cost: 7,
    color: 0x7adf8d,
    diminishingFactor: 0.6,
  },
  {
    id: 'whetstone',
    kind: 'relic',
    name: '磨刀石',
    category: '通用',
    tag: '伤害',
    behavior: '让低频高伤伙伴更有效',
    description: '全伙伴伤害 +12%。',
    cost: 10,
    color: 0xffc857,
    diminishingFactor: 0.75,
  },
  {
    id: 'ammo-pouch',
    kind: 'relic',
    name: '弹药袋',
    category: '通用',
    tag: '攻速',
    behavior: '让高频低伤伙伴更有效',
    description: '全伙伴攻速 +12%。',
    cost: 10,
    color: 0x4d8dff,
    diminishingFactor: 0.75,
  },
  {
    id: 'thorn-armor',
    kind: 'relic',
    name: '荆棘甲',
    category: '通用',
    tag: '反伤',
    behavior: '鼓励近身肉搏',
    description: '受碰撞伤害时反弹 6 伤害。',
    cost: 8,
    color: 0x8aa0a8,
  },
  {
    id: 'life-tree',
    kind: 'relic',
    name: '生命树',
    category: '通用',
    tag: '续航',
    behavior: '鼓励续航型构筑',
    description: '每波结束回复 10 生命。',
    cost: 9,
    color: 0x61d88f,
  },
  {
    id: 'shield-shard',
    kind: 'relic',
    name: '护盾碎片',
    category: '通用',
    tag: '护盾',
    behavior: '减少连续被击的惩罚',
    description: '每 3 秒可免疫一次受伤。',
    cost: 28,
    color: 0xcfd7d9,
    maxStacks: 3,
  },
  {
    id: 'battle-spirit',
    kind: 'relic',
    name: '战意',
    category: '通用',
    tag: '击杀',
    behavior: '让进攻成为防守',
    description: '击杀敌人回复 1 生命。',
    cost: 7,
    color: 0xff7f6e,
  },
  {
    id: 'lightning-chain',
    kind: 'relic',
    name: '闪电链',
    category: '通用',
    tag: '弹道',
    behavior: '让枪手和召唤师有溅射',
    description: '弹道命中时有概率跳电 4 伤害。',
    cost: 12,
    color: 0x8ad8ff,
  },
  {
    id: 'swordmaster-mark',
    kind: 'relic',
    name: '剑豪之证',
    category: '职业',
    tag: '武斗',
    behavior: '鼓励近战贴身',
    description: '武斗伙伴攻击范围 +30%。',
    cost: 10,
    color: 0xffc857,
  },
  {
    id: 'sharpshooter',
    kind: 'relic',
    name: '神射手',
    category: '职业',
    tag: '射击',
    behavior: '鼓励远程稳定输出',
    description: '射击弹速 +25%，弹道穿透 +1。',
    cost: 10,
    color: 0x4d8dff,
  },
  {
    id: 'elemental-heart',
    kind: 'relic',
    name: '元素之心',
    category: '职业',
    tag: '法术',
    behavior: '鼓励 AOE 清群',
    description: '法术范围 +25%。',
    cost: 10,
    color: 0xb56cff,
  },
  {
    id: 'healer-sigil',
    kind: 'relic',
    name: '医者圣印',
    category: '职业',
    tag: '支援',
    behavior: '鼓励回复和保命',
    description: '支援效果 +40%，重装挡伤更频繁。',
    cost: 10,
    color: 0x61d88f,
  },
  {
    id: 'berserker-axe',
    kind: 'relic',
    name: '狂战斧',
    category: '职业',
    tag: '武斗',
    behavior: '鼓励低血量反杀',
    description: '生命低于 40% 时武斗伤害 +35%。',
    cost: 12,
    color: 0xff5d73,
  },
  {
    id: 'barrage',
    kind: 'relic',
    name: '弹幕',
    category: '职业',
    tag: '射击',
    behavior: '鼓励多射击伙伴',
    description: '每个射击伙伴让射击攻速 +8%。',
    cost: 11,
    color: 0x5bb8ff,
  },
  {
    id: 'resonance-crystal',
    kind: 'relic',
    name: '共鸣水晶',
    category: '职业',
    tag: '法术',
    behavior: '鼓励法术叠满',
    description: '法术羁绊激活时法术伤害 +15%。',
    cost: 11,
    color: 0xc891ff,
  },
  {
    id: 'support-protocol',
    kind: 'relic',
    name: '援护协议',
    category: '职业',
    tag: '支援',
    behavior: '鼓励支援多选',
    description: '每个支援伙伴让受伤 -10%。',
    cost: 9,
    color: 0x88e0ad,
  },
  {
    id: 'blood-oath',
    kind: 'relic',
    name: '血誓',
    category: '风险',
    tag: '风险',
    behavior: '用生命上限换输出',
    description: '伤害 +30%，最大生命 -25。',
    cost: 16,
    color: 0xd95b4f,
  },
  {
    id: 'greed',
    kind: 'relic',
    name: '贪婪',
    category: '风险',
    tag: '经济',
    behavior: '用生存换经济',
    description: '材料 +50%，受到伤害 +25%。',
    cost: 7,
    color: 0xf2c15c,
  },
  {
    id: 'glass-cannon',
    kind: 'relic',
    name: '玻璃大炮',
    category: '风险',
    tag: '暴击',
    behavior: '极端追求伤害',
    description: '暴击率 +20%，暴击伤害 2.5 倍，最大生命 -20。',
    cost: 18,
    color: 0xff9f45,
  },
  {
    id: 'lone-wolf',
    kind: 'relic',
    name: '孤狼',
    category: '风险',
    tag: '少伙伴',
    behavior: '鼓励少伙伴高品质',
    description: '每个空伙伴位让伤害 +12%。',
    cost: 16,
    color: 0x9b6cff,
    maxStacks: 3,
  },
  {
    id: 'time-bomb',
    kind: 'relic',
    name: '延时炸弹',
    category: '风险',
    tag: '持续',
    behavior: '鼓励拉长战斗',
    description: '伙伴攻速 +10%，但敌人生命 +15%。',
    cost: 8,
    color: 0xffa333,
  },
  {
    id: 'bloodlust',
    kind: 'relic',
    name: '嗜血',
    category: '风险',
    tag: '连杀',
    behavior: '鼓励连续击杀',
    description: '击杀后短时伤害提高，可叠 3 层。',
    cost: 30,
    color: 0xff5d73,
  },
  {
    id: 'gambler-dice',
    kind: 'relic',
    name: '赌徒骰子',
    category: '风险',
    tag: '随机',
    behavior: '鼓励赌运气',
    description: '每波随机偏向一个伙伴，整体伤害 +15%。',
    cost: 6,
    color: 0xf7df9a,
    maxStacks: 3,
  },
  {
    id: 'forgotten-contract',
    kind: 'relic',
    name: '遗忘契约',
    category: '风险',
    tag: '商店',
    behavior: '用信息换力量',
    description: '伤害 +20%，攻速 +15%，商店少 1 候选。',
    cost: 18,
    color: 0x6d5c38,
    maxStacks: 1,
  },
  {
    id: 'merchant-pass',
    kind: 'relic',
    name: '商人证',
    category: '经济',
    tag: '买卖',
    behavior: '鼓励频繁买卖',
    description: '出售伙伴返还 80%。',
    cost: 26,
    color: 0xe0b15a,
    maxStacks: 1,
  },
  {
    id: 'gold-rush',
    kind: 'relic',
    name: '淘金热',
    category: '经济',
    tag: '前期',
    behavior: '鼓励前期存材料',
    description: '前 3 波材料 +50%，后期材料 -30%。',
    cost: 6,
    color: 0xf2c15c,
  },
  {
    id: 'free-reroll',
    kind: 'relic',
    name: '免费刷新',
    category: '经济',
    tag: '刷新',
    behavior: '降低商店决策成本',
    description: '每波第一次刷新免费。',
    cost: 28,
    color: 0x88ff88,
    maxStacks: 1,
  },
  {
    id: 'investment-return',
    kind: 'relic',
    name: '投资回报',
    category: '经济',
    tag: '存钱',
    behavior: '鼓励攒材料不花',
    description: '每 10 材料让伤害 +2%，最高 +20%。',
    cost: 16,
    color: 0x9ee7a8,
  },
  {
    id: 'small-blade',
    kind: 'relic',
    name: '小刀片',
    category: '通用',
    tag: '固定伤害',
    behavior: '给前期弱输出一个稳定补强',
    description: '伙伴基础伤害 +2。',
    cost: 5,
    color: 0xc8c8c8,
  },
  {
    id: 'field-ration',
    kind: 'relic',
    name: '野战口粮',
    category: '通用',
    tag: '续航',
    behavior: '让低价回复可重复堆叠',
    description: '每波结束回复 6 生命。',
    cost: 5,
    color: 0xc8c8c8,
  },
  {
    id: 'pickup-bell',
    kind: 'relic',
    name: '拾荒铃',
    category: '通用',
    tag: '拾取',
    behavior: '让捡材料更安全',
    description: '拾取范围 +20%。',
    cost: 5,
    color: 0xc8c8c8,
  },
  {
    id: 'bronze-scope',
    kind: 'relic',
    name: '青铜瞄具',
    category: '通用',
    tag: '弹道',
    behavior: '改善远程命中手感',
    description: '弹道速度 +15%。',
    cost: 5,
    color: 0xc8c8c8,
  },
  {
    id: 'lucky-chip',
    kind: 'relic',
    name: '幸运筹码',
    category: '经济',
    tag: '材料',
    behavior: '低价增加经济滚动',
    description: '材料获取 +12%。',
    cost: 6,
    color: 0xc8c8c8,
  },
  {
    id: 'quick-spring',
    kind: 'relic',
    name: '速射弹簧',
    category: '通用',
    tag: '攻速',
    behavior: '便宜的小攻速件',
    description: '伙伴攻速 +8%。',
    cost: 6,
    color: 0xc8c8c8,
  },
  {
    id: 'rusty-nail',
    kind: 'relic',
    name: '生锈钉',
    category: '通用',
    tag: '固定伤害',
    behavior: '让第一波遗物流立即补到一点伤害',
    description: '伙伴基础伤害 +1。',
    cost: 3,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'cloth-boots',
    kind: 'relic',
    name: '布鞋',
    category: '通用',
    tag: '机动',
    behavior: '低价改善早期走位',
    description: '移动速度 +6%。',
    cost: 3,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'tiny-magnet',
    kind: 'relic',
    name: '小磁片',
    category: '通用',
    tag: '拾取',
    behavior: '低价降低捡材料风险',
    description: '拾取范围 +10%。',
    cost: 3,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'copper-coin',
    kind: 'relic',
    name: '铜币',
    category: '经济',
    tag: '材料',
    behavior: '遗物流前期经济启动件',
    description: '材料获取 +6%。',
    cost: 4,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'training-weight',
    kind: 'relic',
    name: '训练沙袋',
    category: '通用',
    tag: '生命',
    behavior: '便宜补一点容错',
    description: '最大生命 +8。',
    cost: 4,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'wooden-shield',
    kind: 'relic',
    name: '木盾',
    category: '通用',
    tag: '减伤',
    behavior: '给高难度早期留一点生存空间',
    description: '受到伤害 -5%。',
    cost: 4,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'herb-tea',
    kind: 'relic',
    name: '草药茶',
    category: '通用',
    tag: '续航',
    behavior: '低价波末回复，稳定续航路线',
    description: '每波结束回复 4 生命。',
    cost: 4,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'spark-plug',
    kind: 'relic',
    name: '火花塞',
    category: '通用',
    tag: '攻速',
    behavior: '低价补一点攻击频率',
    description: '伙伴攻速 +5%。',
    cost: 5,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'chalk-mark',
    kind: 'relic',
    name: '粉笔标记',
    category: '通用',
    tag: '暴击',
    behavior: '给早期暴击路线一个小入口',
    description: '暴击率 +4%。',
    cost: 5,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'warm-glove',
    kind: 'relic',
    name: '保暖手套',
    category: '职业',
    tag: '武斗',
    behavior: '低价支持武斗前期成型',
    description: '武斗攻击范围 +8%。',
    cost: 5,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'paper-fan',
    kind: 'relic',
    name: '纸扇',
    category: '职业',
    tag: '法术',
    behavior: '低价支持法术范围成长',
    description: '法术范围 +8%。',
    cost: 5,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'focus-thread',
    kind: 'relic',
    name: '专注线',
    category: '职业',
    tag: '射击',
    behavior: '低价支持射击弹道手感',
    description: '射击弹道速度 +8%。',
    cost: 5,
    color: 0xc8c8c8,
    diminishingFactor: 0.6,
  },
  {
    id: 'sharp-blade',
    kind: 'relic',
    name: '锋利刀锋',
    category: '通用',
    tag: '固定伤害',
    behavior: '让多段攻击吃到固定伤害成长',
    description: '伙伴基础伤害 +3。',
    cost: 10,
    color: 0x4dff88,
  },
  {
    id: 'precision-ring',
    kind: 'relic',
    name: '精准戒指',
    category: '通用',
    tag: '暴击',
    behavior: '给任何构筑补暴击路线',
    description: '暴击率 +8%。',
    cost: 10,
    color: 0x4dff88,
  },
  {
    id: 'power-core',
    kind: 'relic',
    name: '动力核心',
    category: '通用',
    tag: '百分比伤害',
    behavior: '中期通用百分比成长',
    description: '全伙伴伤害 +18%。',
    cost: 12,
    color: 0x4dff88,
  },
  {
    id: 'guardian-buckle',
    kind: 'relic',
    name: '守护扣带',
    category: '通用',
    tag: '减伤',
    behavior: '给近身构筑留容错',
    description: '受到伤害 -12%。',
    cost: 11,
    color: 0x4dff88,
  },
  {
    id: 'collector-bag',
    kind: 'relic',
    name: '收藏家背包',
    category: '经济',
    tag: '材料',
    behavior: '中期经济成长件',
    description: '材料获取 +22%。',
    cost: 12,
    color: 0x4dff88,
  },
  {
    id: 'long-barrel',
    kind: 'relic',
    name: '长枪管',
    category: '职业',
    tag: '射击',
    behavior: '让射击构筑更像远程火力',
    description: '射击伤害 +18%，弹道速度 +15%。',
    cost: 16,
    color: 0x4d8dff,
  },
  {
    id: 'arcane-ink',
    kind: 'relic',
    name: '奥术墨水',
    category: '职业',
    tag: '法术',
    behavior: '让法术走范围和伤害双成长',
    description: '法术伤害 +18%，法术范围 +10%。',
    cost: 16,
    color: 0x4d8dff,
  },
  {
    id: 'piercing-needle',
    kind: 'relic',
    name: '穿甲针',
    category: '职业',
    tag: '射击',
    behavior: '让弹道处理密集敌群',
    description: '射击弹道穿透 +2。',
    cost: 18,
    color: 0x4d8dff,
  },
  {
    id: 'overload-coil',
    kind: 'relic',
    name: '过载线圈',
    category: '风险',
    tag: '攻速',
    behavior: '用承伤风险换攻速爆发',
    description: '伙伴攻速 +25%，受到伤害 +10%。',
    cost: 18,
    color: 0x4d8dff,
  },
  {
    id: 'vampire-fang',
    kind: 'relic',
    name: '吸血獠牙',
    category: '风险',
    tag: '击杀',
    behavior: '鼓励激进连续击杀',
    description: '击杀回复 +2，最大生命 -10。',
    cost: 17,
    color: 0x4d8dff,
  },
  {
    id: 'gold-magnet',
    kind: 'relic',
    name: '黄金磁铁',
    category: '经济',
    tag: '拾取',
    behavior: '把拾取和经济绑成一条成长线',
    description: '拾取范围 +75%，材料获取 +15%。',
    cost: 19,
    color: 0x4d8dff,
  },
  {
    id: 'soul-link',
    kind: 'relic',
    name: '灵魂链接',
    category: '职业',
    tag: '支援',
    behavior: '让支援构筑同时补生存和输出',
    description: '击杀回复 +2，支援效果 +25%，受到伤害 -8%。',
    cost: 30,
    color: 0xb56cff,
  },
  {
    id: 'echo-chamber',
    kind: 'relic',
    name: '回声室',
    category: '通用',
    tag: '弹道',
    behavior: '把连锁闪电做成高稀有核心',
    description: '弹道命中时更容易跳电，跳电伤害 +6。',
    cost: 30,
    color: 0xb56cff,
  },
  {
    id: 'armor-piercer',
    kind: 'relic',
    name: '破甲核心',
    category: '职业',
    tag: '射击',
    behavior: '让射击构筑同时拿穿透和伤害',
    description: '射击穿透 +2，全伙伴伤害 +10%。',
    cost: 32,
    color: 0xb56cff,
  },
  {
    id: 'endless-vault',
    kind: 'relic',
    name: '无尽宝库',
    category: '经济',
    tag: '商店',
    behavior: '让无限遗物构筑越买越顺',
    description: '商店价格 -10%，最多降到 80%。',
    cost: 50,
    color: 0xffd700,
  },
  {
    id: 'world-resonance',
    kind: 'relic',
    name: '万物共鸣',
    category: '通用',
    tag: '全能',
    behavior: '传说级全构筑通用核心',
    description: '伤害 +30%，攻速 +20%，最大生命 +30。',
    cost: 58,
    color: 0xffd700,
  },
  {
    id: 'relic-printer',
    kind: 'relic',
    name: '遗物打印机',
    category: '经济',
    tag: '无限',
    behavior: '把同名无限叠加推到极致',
    description: '材料获取 +35%，全伙伴伤害 +15%。',
    cost: 62,
    color: 0xffd700,
  },
] satisfies readonly RawRelicDefinition[]).map((relic) => ({
  ...relic,
  rarity: RELIC_RARITY_BY_ID[relic.id],
}));

export function getRelicDefinition(id: RelicId) {
  const relic = RELIC_CATALOG.find((candidate) => candidate.id === id);
  if (!relic) {
    throw new Error(`Unknown relic: ${id}`);
  }
  return relic;
}

export function isRelicId(value: string): value is RelicId {
  return RELIC_CATALOG.some((relic) => relic.id === value);
}

export function isMartialPartner(type: PartnerType) {
  return type === 'sword' || type === 'assassin' || type === 'tank';
}

export function isShootingPartner(type: PartnerType) {
  return type === 'gunner' || type === 'engineer';
}

export function isMagicPartner(type: PartnerType) {
  return type === 'mage' || type === 'summoner';
}

export function isSupportPartner(type: PartnerType) {
  return type === 'nurse' || type === 'engineer';
}
